import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export const inputCls =
  "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none";
export const btnPrimary =
  "bg-ops text-white text-sm px-4 py-2 rounded-lg hover:bg-ops/90 disabled:opacity-50";
export const btnSecondary =
  "text-sm text-primary border border-gray-300 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50";

export function Section({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-lg font-bold text-primary">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>{children}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm text-muted">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted mt-1">{hint}</span>}
    </label>
  );
}

export function ErrorText({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p data-testid={testId} role="alert" className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-lg px-3 py-2">
      {children}
    </p>
  );
}

/** 招待リンクの表示。data-testid="invite-link" の中身は URL の文字列だけにする */
export function InviteLinkBox({ url }: { url: string }) {
  const { t } = useTranslation(["common", "admin"]);
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="mt-3 bg-sakura/10 border border-sakura rounded-lg p-3">
      <div className="text-xs text-muted mb-1">{t("admin:invite_link_label")}</div>
      <div className="flex items-center gap-2 flex-wrap">
        <code data-testid="invite-link" className="text-sm font-mono text-primary break-all select-all">{url}</code>
        <button type="button" onClick={copy} className={btnSecondary}>
          {copied ? t("common:copied") : t("common:copy")}
        </button>
      </div>
    </div>
  );
}
